import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `jobs:` scope (F14/E11 Job Progress
 * surface), following the exact `web:` precedent (`apps/web/src/i18n/
 * webCatalog.ts` + `useWebT.ts`) — this is NOT a second i18n runtime
 * (E18/§13.3 ban): same ICU-via-`intl-messageformat` mechanism as
 * `@xdocs/i18n`, just physically scoped here because `packages/i18n/**`
 * (where these keys structurally belong as a `jobs` catalog scope) is
 * outside this task's `apps/web/src/features/jobs/**` ownership boundary.
 *
 * OPEN ISSUE: the `@xdocs/i18n` (F0j) owner should fold this into
 * `packages/i18n/src/catalogs/{en,ro}/jobs.json` and delete this file +
 * `useJobsT`, at which point call sites become plain `useI18n().t('jobs:…')`.
 */
const en = {
  'status.queued': 'Queued',
  'status.running': 'Generating…',
  'status.succeeded': 'Done',
  'status.failed': 'Failed',
  'status.canceled': 'Canceled',
  'stage.research_sources': 'Researching sources',
  'stage.download': 'Downloading sources',
  'stage.extract_text': 'Extracting text',
  'stage.derive_outline': 'Drafting outline',
  'stage.plan_chapters': 'Planning chapters',
  'stage.batch_generate_chapters': 'Writing chapters',
  'stage.assemble_manuscript': 'Assembling manuscript',
  'stage.write_version': 'Saving version',
  'stage.critique': 'Reviewing',
  'stage.emit_patches': 'Preparing fixes',
  'stage.apply_patches_deterministic': 'Applying fixes',
  'stage.finalize_version': 'Finalizing',
  'stage.illustrate': 'Illustrating',
  'stage.render': 'Rendering deliverables',
  'stage.fetch_or_receive': 'Receiving file',
  'stage.thumbnail_sharp': 'Generating thumbnail',
  'stage.register_file': 'Finishing up',
  'step.done': 'Done',
  'step.running': 'In progress',
  'step.queued': 'Queued',
  'step.error': 'Error',
  'progress.title': 'Generation progress',
  'hero.title.queued': 'Getting ready…',
  'hero.title.running': 'Generating your document…',
  'hero.title.succeeded': 'Generation complete',
  'hero.title.failed': 'Generation failed',
  'hero.title.canceled': 'Generation canceled',
  'progress.attempt': 'Attempt {attempt} of {max}',
  'progress.willRetry': 'This step failed but will retry automatically.',
  'progress.terminalFailed': 'This job failed and won’t retry again.',
  'progress.checkpointsTitle': 'Stages',
  'logs.title': 'Activity log',
  'logs.empty': 'No activity yet.',
  'logs.loadMore': 'Load earlier activity',
  'logs.loadMoreError': 'Couldn’t load earlier activity.',
  'error.title': 'Couldn’t load this job.',
  'error.retry': 'Retry',
  'reattaching': 'Reconnecting to this job…',
  'unknown': 'Job status unavailable.',
} as const;

const ro: Record<keyof typeof en, string> = {
  'status.queued': 'În așteptare',
  'status.running': 'Se generează…',
  'status.succeeded': 'Finalizat',
  'status.failed': 'Eșuat',
  'status.canceled': 'Anulat',
  'stage.research_sources': 'Se cercetează sursele',
  'stage.download': 'Se descarcă sursele',
  'stage.extract_text': 'Se extrage textul',
  'stage.derive_outline': 'Se redactează structura',
  'stage.plan_chapters': 'Se planifică capitolele',
  'stage.batch_generate_chapters': 'Se scriu capitolele',
  'stage.assemble_manuscript': 'Se asamblează manuscrisul',
  'stage.write_version': 'Se salvează versiunea',
  'stage.critique': 'Se revizuiește',
  'stage.emit_patches': 'Se pregătesc corecțiile',
  'stage.apply_patches_deterministic': 'Se aplică corecțiile',
  'stage.finalize_version': 'Se finalizează',
  'stage.illustrate': 'Se ilustrează',
  'stage.render': 'Se generează fișierele',
  'stage.fetch_or_receive': 'Se primește fișierul',
  'stage.thumbnail_sharp': 'Se generează miniatura',
  'stage.register_file': 'Se finalizează',
  'step.done': 'Finalizat',
  'step.running': 'În curs',
  'step.queued': 'În așteptare',
  'step.error': 'Eroare',
  'progress.title': 'Progres generare',
  'hero.title.queued': 'Se pregătește…',
  'hero.title.running': 'Se generează documentul tău…',
  'hero.title.succeeded': 'Generare finalizată',
  'hero.title.failed': 'Generarea a eșuat',
  'hero.title.canceled': 'Generare anulată',
  'progress.attempt': 'Încercarea {attempt} din {max}',
  'progress.willRetry': 'Acest pas a eșuat, dar va reîncerca automat.',
  'progress.terminalFailed': 'Această sarcină a eșuat și nu va mai reîncerca.',
  'progress.checkpointsTitle': 'Etape',
  'logs.title': 'Jurnal activitate',
  'logs.empty': 'Nicio activitate încă.',
  'logs.loadMore': 'Încarcă activitatea anterioară',
  'logs.loadMoreError': 'Nu s-a putut încărca activitatea anterioară.',
  'error.title': 'Nu am putut încărca această sarcină.',
  'error.retry': 'Reîncearcă',
  'reattaching': 'Se reconectează la această sarcină…',
  'unknown': 'Starea sarcinii nu este disponibilă.',
};

export const JOBS_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type JobsCatalogKey = keyof typeof en;
